import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import config from '../../config';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Divider,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Modern orders tab component
 */
const CANCELLATION_FEE = 1450;

const OrdersTab = ({
  organization,
  orderTypeTab,
  onOrderTypeTabChange,
  adOrderSearchTerm,
  onAdOrderSearchChange,
  adOrderStatusFilter,
  onAdOrderStatusFilterChange,
  schoolOrderSearchTerm,
  onSchoolOrderSearchChange,
  schoolOrderPaymentFilter,
  onSchoolOrderPaymentFilterChange,
  filterAdOrders,
  filterSchoolOrders,
  onOrderClick,
  onSchoolOrderCancelled
}) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  // -- Cancel dialog state ---------------------------------------------------
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    order: null,
    loading: false,
    error: null,
    // form fields
    cancellationReason: '',
    isEarlyTermination: false,
    paidModelCost: '',
    agreementEndDate: new Date().toISOString().split('T')[0],
    // computed
    result: null
  });

  const openCancelDialog = (order) => {
    setCancelDialog({
      open: true,
      order,
      loading: false,
      error: null,
      cancellationReason: '',
      isEarlyTermination: false,
      paidModelCost: '',
      agreementEndDate: new Date().toISOString().split('T')[0],
      result: null
    });
  };

  const closeCancelDialog = () => {
    if (cancelDialog.loading) return;
    setCancelDialog(prev => ({ ...prev, open: false }));
  };

  // Compute preview of cancellation fee
  const computeFeePreview = () => {
    if (!cancelDialog.isEarlyTermination || !cancelDialog.paidModelCost) return null;
    const annualCost = parseFloat(cancelDialog.paidModelCost);
    if (isNaN(annualCost) || annualCost <= 0) return null;

    const order = cancelDialog.order;
    const orderEnd = order?.endDate ? new Date(order.endDate) : null;
    const today = new Date();
    let remainingMonths = 0;
    if (orderEnd && orderEnd > today) {
      const msLeft = orderEnd.getTime() - today.getTime();
      remainingMonths = msLeft / (1000 * 60 * 60 * 24 * 30.4375);
    }
    const proRata = parseFloat(((annualCost / 12) * remainingMonths).toFixed(2));
    const total = parseFloat((proRata + CANCELLATION_FEE).toFixed(2));
    return { remainingMonths: remainingMonths.toFixed(1), proRata, total };
  };

  const handleConfirmCancel = async () => {
    if (!cancelDialog.cancellationReason.trim()) {
      setCancelDialog(prev => ({ ...prev, error: 'Please provide a cancellation reason.' }));
      return;
    }
    if (cancelDialog.isEarlyTermination && !cancelDialog.paidModelCost) {
      setCancelDialog(prev => ({ ...prev, error: 'Please enter the annual paid model cost to calculate the fee.' }));
      return;
    }

    setCancelDialog(prev => ({ ...prev, loading: true, error: null }));
    try {
      const token = await getToken();
      const response = await axios.post(
        `${config.apiUrl}/api/cancelled-schools/cancel/${cancelDialog.order.id}`,
        {
          cancellationReason: cancelDialog.cancellationReason,
          isEarlyTermination: cancelDialog.isEarlyTermination,
          paidModelCost: cancelDialog.isEarlyTermination ? parseFloat(cancelDialog.paidModelCost) : undefined,
          agreementEndDate: cancelDialog.agreementEndDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        setCancelDialog(prev => ({ ...prev, loading: false, result: response.data.data }));
        if (onSchoolOrderCancelled) onSchoolOrderCancelled(cancelDialog.order.id);
      } else {
        setCancelDialog(prev => ({ ...prev, loading: false, error: response.data?.message || 'Cancellation failed.' }));
      }
    } catch (err) {
      setCancelDialog(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || err.message || 'Cancellation failed.'
      }));
    }
  };

  const feePreview = computeFeePreview();

  // Format the order value as currency
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Get order status chip color
  const getStatusChipColor = (status) => {
    if (!status) return 'default';
    
    status = status.toLowerCase();
    if (status.includes('won') || status.includes('approved') || status === 'active') {
      return 'success';
    } else if (status.includes('lost') || status.includes('rejected') || status === 'inactive') {
      return 'error';
    } else if (status.includes('design') || status.includes('waiting') || status.includes('pending')) {
      return 'warning';
    }
    return 'primary';
  };

  // Handle view all orders click
  const handleViewAllOrders = () => {
    if (organization?.id) {
      navigate(`/app/organizations/${organization.id}/orders`);
    }
  };

  // Helper function to calculate order value
  const calculateOrderValue = (order) => {
    // Calculate total from audiences if available
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      const total = order.audiences.reduce((sum, audience) => {
        const price = parseFloat(audience.price || 0);
        const discount = parseFloat(audience.discount || 0);
        return sum + Math.max(0, price - discount);
      }, 0);
      if (total > 0) {
        return total.toFixed(2);
      }
    }
    
    // Fall back to totalPrice or value fields
    return order.totalPrice || order.total_price || order.value || '0.00';
  };

  // Helper function to get business organization name for school orders
  const getBusinessOrganizationName = (order) => {
    // organizationData is populated server-side from the organizations JOIN
    if (order.organizationData && order.organizationData.name) {
      return order.organizationData.name;
    }
    if (order.businessName) {
      return order.businessName;
    }
    // advertiserOrgName is set by the server-side JOIN on organizations
    if (order.advertiserOrgName) {
      return order.advertiserOrgName;
    }
    // For orders where the currently-viewed organization is the advertiser,
    // its own name is the business organisation name
    if (order.relationshipType === 'business' && organization?.name) {
      return organization.name;
    }
    return 'Unknown Business';
  };

  // Helper function to get school name for business organization orders
  const getSchoolName = (order) => {
    // For orders in business organizations, we need to show the school(s) this order is for
    if (order.schoolName) {
      return order.schoolName;
    }
    return 'N/A';
  };

  // Helper function to get channels from audiences
  const getChannels = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      const channels = [...new Set(order.audiences
        .map(audience => audience.channel)
        .filter(channel => channel))];
      return channels.length > 0 ? channels.join(', ') : 'N/A';
    }
    return 'N/A';
  };

  // Helper function to get ad types from audiences
  const getAdTypes = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      const adTypes = [...new Set(order.audiences
        .map(audience => audience.adType || audience.ad_type)
        .filter(adType => adType))];
      return adTypes.length > 0 ? adTypes.join(', ') : 'N/A';
    }
    return order.productType || 'N/A';
  };

  // Helper function to get duration from audiences
  const getDuration = (order) => {
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      // Get the earliest start date and latest end date
      let startDate = null;
      let endDate = null;
      
      order.audiences.forEach(audience => {
        if (audience.startDate) {
          const date = new Date(audience.startDate);
          if (!startDate || date < startDate) {
            startDate = date;
          }
        }
        if (audience.endDate) {
          const date = new Date(audience.endDate);
          if (!endDate || date > endDate) {
            endDate = date;
          }
        }
      });
      
      if (startDate && endDate) {
        return formatDateRange(startDate, endDate);
      }
    }
    return 'N/A';
  };

  // Helper function to get relationship label
  const getRelationshipLabel = (relationshipType) => {
    switch (relationshipType) {
      case 'business':
        return 'Business';
      case 'audience':
        return 'Audience';
      case 'school':
        return 'School';
      default:
        return 'Unknown';
    }
  };

  // Helper function to get relationship chip color
  const getRelationshipChipColor = (relationshipType) => {
    switch (relationshipType) {
      case 'business':
        return 'primary';
      case 'audience':
        return 'secondary';
      case 'school':
        return 'info';
      default:
        return 'default';
    }
  };

  // Debug logging to check school orders
  if (organization?.orders) {
    const schoolOrders = organization.orders.filter(order => 
      (order.orderType && order.orderType.toLowerCase() === 'school') || 
      (order.schoolName)
    );
    
    const adOrders = organization.orders.filter(order => 
      order.orderType && ['advertising', 'ad', 'advert', 'advertisement'].includes(order.orderType.toLowerCase())
    );
    
    // Log all unique order types to help with debugging
    const uniqueOrderTypes = [...new Set(organization.orders.map(order => order.orderType))];
    
    // Log the first ad order for debugging
    if (adOrders.length > 0) {
    }
  }

  // Check if this is a school organization (not based on orders, only on organization type)
  const isSchoolOrganization = 
    organization?.type === 'School' || 
    (organization?.type && organization.type.toLowerCase().includes('school')) ||
    organization?.businessType === 'School' || 
    (organization?.businessType && organization.businessType.toLowerCase().includes('school')) ||
    organization?.isSchool === 1 || 
    organization?.isSchool === true;

  // Check if this is a business organization (not a school)
  const isBusinessOrganization = !isSchoolOrganization;

  // If orderTypeTab is set to a tab that isn't available, reset it to 0
  useEffect(() => {
    if (orderTypeTab === 1 && !isSchoolOrganization) {
      onOrderTypeTabChange(null, 0);
    }
  }, [orderTypeTab, isSchoolOrganization, onOrderTypeTabChange]);

  return (
    <>
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={orderTypeTab}
          onChange={onOrderTypeTabChange}
          sx={{ 
            mb: 2,
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1,
              mx: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontWeight: 600
              }
            }
          }}
        >
          <Tab label="Advertising Orders" />
          {isSchoolOrganization && <Tab label="School Orders" />}
        </Tabs>
      </Box>
      
      {/* Advertising Orders Tab */}
      {orderTypeTab === 0 && (
        <>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Advertising Orders
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search orders..."
                value={adOrderSearchTerm}
                onChange={(e) => onAdOrderSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
                sx={{ width: 250, mr: 2 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={adOrderStatusFilter}
                  onChange={(e) => onAdOrderStatusFilterChange(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {organization?.orders && 
                    [...new Set(organization.orders
                      .filter(order => ['advertising', 'ad', 'advert', 'advertisement'].includes(order.orderType?.toLowerCase()))
                      .map(order => order.status)
                    )]
                    .filter(status => status) // Filter out null/undefined
                    .map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme => theme.palette.divider}` }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Relationship</TableCell>
                  {isSchoolOrganization && <TableCell>Business Organisation</TableCell>}
                  {isBusinessOrganization && <TableCell>School</TableCell>}
                  <TableCell>Channel</TableCell>
                  <TableCell>Ad Type</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organization?.orders && organization.orders.length > 0 ? (
                  filterAdOrders(organization.orders).map((order) => (
                    <TableRow 
                      key={order.id} 
                      hover
                      onClick={() => onOrderClick(order)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {order.id}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getRelationshipLabel(order.relationshipType)} 
                          color={getRelationshipChipColor(order.relationshipType)}
                          size="small"
                          sx={{ 
                            height: '24px', 
                            fontWeight: 500,
                            borderRadius: '4px'
                          }}
                        />
                      </TableCell>
                      {isSchoolOrganization && (
                        <TableCell>
                          <Typography variant="body2" color="primary.main" fontWeight={500}>
                            {getBusinessOrganizationName(order)}
                          </Typography>
                        </TableCell>
                      )}
                      {isBusinessOrganization && (
                        <TableCell>
                          <Typography variant="body2" color="info.main" fontWeight={500}>
                            {getSchoolName(order)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2">
                          {getChannels(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getAdTypes(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {getDuration(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight={600} 
                          color="success.dark"
                        >
                          {formatCurrency(calculateOrderValue(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status || 'N/A'} 
                          color={getStatusChipColor(order.status)}
                          size="small"
                          sx={{ 
                            height: '24px', 
                            fontWeight: 500,
                            borderRadius: '4px'
                          }}
                        />
                      </TableCell>
                      <TableCell>{order.salesPersonName || 'Not assigned'}</TableCell>
                      <TableCell>
                        {order.createdAt ? formatDate(order.createdAt) : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isSchoolOrganization ? 11 : 10} align="center">
                      No advertising orders found for this organization
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* View all orders button */}
          {organization?.orders && filterAdOrders(organization.orders).length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleViewAllOrders}
              >
                View All Orders
              </Button>
            </Box>
          )}
        </>
      )}
      
      {/* School Orders Tab */}
      {orderTypeTab === 1 && (
        <>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              School Orders
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search school orders..."
                value={schoolOrderSearchTerm}
                onChange={(e) => onSchoolOrderSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
                sx={{ width: 250, mr: 2 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Payment Model</InputLabel>
                <Select
                  value={schoolOrderPaymentFilter}
                  onChange={(e) => onSchoolOrderPaymentFilterChange(e.target.value)}
                  label="Payment Model"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Models</MenuItem>
                  {organization?.orders && 
                    [...new Set(organization.orders
                      .filter(order => order.orderType === 'school' || order.schoolName)
                      .map(order => order.paymentModel)
                    )]
                    .filter(model => model) // Filter out null/undefined
                    .map(model => (
                      <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme => theme.palette.divider}` }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organization?.orders && organization.orders.length > 0 ? (
                  filterSchoolOrders(organization.orders).map((order) => {
                    const baseUrl = config.apiUrl;
                    const invoiceLink = order.invoiceUrl ? `${baseUrl}/proof/invoice/${order.invoiceUrl}` : null;
                    const certLink = order.certToken ? `${baseUrl}/reorders/certificates/cert-${order.certToken}.html` : null;
                    const isExpired = order.endDate && new Date(order.endDate) < new Date(new Date().toDateString());
                    const statusLabel = isExpired ? 'Expired' : order.active === false ? 'Inactive' : 'Active';
                    const statusColor = isExpired ? 'warning' : order.active === false ? 'error' : 'success';
                    const isCancelled = order.status && order.status.toLowerCase() === 'cancelled';
                    return (
                      <TableRow key={order.id || order.schoolOrderId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{order.schoolName || 'Unnamed School'}</Typography>
                          {order.invoiceNumber && <Typography variant="caption" color="text.secondary">{order.invoiceNumber}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.contactName || order.contactPerson || 'N/A'}</Typography>
                          {order.email && <Typography variant="caption" color="text.secondary">{order.email}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip label={statusLabel} color={statusColor} size="small" sx={{ height: '24px', fontWeight: 500, borderRadius: '4px' }} />
                        </TableCell>
                        <TableCell>{order.endDate ? formatDate(order.endDate) : 'N/A'}</TableCell>
                        <TableCell>${parseFloat(order.totalPrice || order.total_price || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {invoiceLink && (
                              <Tooltip title="View Invoice">
                                <Button size="small" variant="outlined" color="primary"
                                  onClick={(e) => { e.stopPropagation(); window.open(invoiceLink, '_blank'); }}
                                  sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>
                                  Invoice
                                </Button>
                              </Tooltip>
                            )}
                            {certLink && (
                              <Tooltip title="View Order Certificate">
                                <Button size="small" variant="outlined" color="secondary"
                                  onClick={(e) => { e.stopPropagation(); window.open(certLink, '_blank'); }}
                                  sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>
                                  Certificate
                                </Button>
                              </Tooltip>
                            )}
                            {!invoiceLink && !certLink && (
                              <Typography variant="caption" color="text.disabled">�</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {!isCancelled && (
                            <Tooltip title="Cancel Order">
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon fontSize="small" />}
                                onClick={(e) => { e.stopPropagation(); openCancelDialog(order); }}
                                sx={{ fontSize: '0.7rem', px: 1 }}
                              >
                                Cancel Order
                              </Button>
                            </Tooltip>
                          )}
                          {isCancelled && (
                            <Chip label="Cancelled" color="error" size="small" sx={{ height: '22px', fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No school orders found for this organization
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* View all orders button */}
          {organization?.orders && filterSchoolOrders(organization.orders).length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={handleViewAllOrders}
              >
                View All School Orders
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>

      {/* -- Cancel Order Dialog ----------------------------------------------- */}
      <Dialog
        open={cancelDialog.open}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={cancelDialog.loading}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {cancelDialog.result ? 'Order Cancelled' : 'Cancel School Order'}
        </DialogTitle>

        {cancelDialog.result ? (
          <>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                The school order has been cancelled and added to the Cancelled Schools list.
                Any active ad order salespeople have been notified.
              </Alert>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Agreement End Date</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.agreementEndDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Account Closure Date (1 month later)</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.accountClosureDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Content Deleted Date (3 months after closure)</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatDate(cancelDialog.result.contentDeletedDate)}</Typography>
                </Box>
                {cancelDialog.result.totalCancellationFee != null && (
                  <>
                    <Divider />
                    <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                      <Typography variant="caption" color="error.main">Early Termination Fee</Typography>
                      <Typography variant="h6" color="error.main" fontWeight={700}>
                        ${parseFloat(cancelDialog.result.totalCancellationFee).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        An early termination fee of $1,450.00 plus the pro rata distribution costs of the service
                        for the remaining duration of the 12 month service is applicable.
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={closeCancelDialog}>Close</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to cancel the school order for{' '}
                <strong>{cancelDialog.order?.schoolName || 'this school'}</strong>?
                This will notify any salespeople with active ad orders linked to this school.
              </DialogContentText>

              {cancelDialog.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{cancelDialog.error}</Alert>
              )}

              <Stack spacing={2.5}>
                <TextField
                  label="Agreement End Date"
                  type="date"
                  value={cancelDialog.agreementEndDate}
                  onChange={(e) => setCancelDialog(prev => ({ ...prev, agreementEndDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText="The date the school cancels or agreement ends"
                  fullWidth
                  size="small"
                  required
                />

                <TextField
                  label="Reason for cancellation / non-renewal"
                  value={cancelDialog.cancellationReason}
                  onChange={(e) => setCancelDialog(prev => ({ ...prev, cancellationReason: e.target.value }))}
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g. School did not renew � budget constraints"
                />

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={cancelDialog.isEarlyTermination}
                      onChange={(e) => setCancelDialog(prev => ({ ...prev, isEarlyTermination: e.target.checked, paidModelCost: '' }))}
                      color="error"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Early termination (mid-order cancellation � cancellation fee applies)
                    </Typography>
                  }
                />

                {cancelDialog.isEarlyTermination && (
                  <Box sx={{ pl: 1 }}>
                    <TextField
                      label="Annual cost under the Paid model ($)"
                      value={cancelDialog.paidModelCost}
                      onChange={(e) => setCancelDialog(prev => ({ ...prev, paidModelCost: e.target.value }))}
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      size="small"
                      fullWidth
                      required
                      helperText={
                        cancelDialog.order?.paymentModel === 'Ad Supported'
                          ? 'School is on Ad Supported � enter the equivalent Paid model annual cost'
                          : 'Enter the annual cost under the Paid model'
                      }
                    />

                    {feePreview && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                        <Typography variant="caption" color="warning.dark" display="block" gutterBottom>
                          Estimated Cancellation Fee
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Annual paid cost: <strong>${parseFloat(cancelDialog.paidModelCost).toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Remaining: <strong>{feePreview.remainingMonths} months</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pro-rata distribution cost: <strong>${feePreview.proRata.toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Early termination fee: <strong>$1,450.00</strong>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" fontWeight={700} color="error.main">
                          Total: ${feePreview.total.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          An early termination fee of $1,450.00 plus the pro rata distribution costs of
                          the service for the remaining duration of the 12 month service is applicable.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeCancelDialog} disabled={cancelDialog.loading}>
                Keep Order
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmCancel}
                disabled={cancelDialog.loading}
                startIcon={cancelDialog.loading ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
              >
                {cancelDialog.loading ? 'Cancelling�' : 'Confirm Cancellation'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default OrdersTab; 